import {
    Badge,
    Card,
    createStyles,
    Flex,
    getStylesRef,
    Group,
    Image,
    PaperProps,
    Progress,
    Stack,
    Text,
} from "@mantine/core";
import { ICampaign, ICurrencyRates } from "../types";
import { Link } from "react-router-dom";
import { formatCurrency } from "../utils/FormValidator";
import { useEffect, useState } from "react";
import { makeGetRequest } from "../services/httpHelper";
import { convertCurrency, isErrorObject } from "../utils/utils";
import ErrorHandler from "../pages/services/ErrorHandler";

const useStyles = createStyles((theme) => ({
    card: {
        position: "relative",
        padding: theme.spacing.lg,
        backdropFilter: `blur(16px) saturate(180%)`,
        backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : `rgba(255, 255, 255, 0.75)`,
        border: `2px solid rgba(209, 213, 219, 0.3)`,

        [`&:hover .${getStylesRef("image")}`]: {
            transform: "scale(1.03)",
        },

        "&:hover": {
            boxShadow: theme.shadows.xl,
            border: `2px solid ${theme.colors.primary[7]}`,
            backgroundColor: theme.colors.primary[0],
            transition: "all 150ms ease",
        },
    },

    title: {
        marginTop: theme.spacing.md,
    },

    image: {
        ref: getStylesRef("image"),
        transition: "transform 150ms ease",
    },
}));

interface IProps extends PaperProps {
    data: ICampaign;
    showActions?: boolean;
}

// Calculate progress percentage
const getProgress = (raised: number, target: number): number => {
    return (raised / target) * 100;
};

const CampaignCard = ({ data, showActions }: IProps) => {
    const [exchangeRates, setExchangeRates] = useState<ICurrencyRates>();
    const [error, setError] = useState<string | null>(null);
    const [userCurrencyCode, setUserCurrencyCode] = useState("USD");



    const { classes } = useStyles();
    const { image, id, title, raised,  description, category, country, target, currency } = data;
    const linkProps = { to: `/campaigns/${id}`, rel: "noopener noreferrer" };

    // Calculate total raised from donations
    const totalRaised = () => {
        const donations = data.donations;

       // return donations?.reduce((total, d) => total + parseFloat(d.donation_amount), 0) || 0;

        let total =0;
        const campaignDonations = donations && donations.filter((donation)=>donation.campaign_id===id);
        campaignDonations && campaignDonations.map((d)=>{
            if(currency === d.donation_currency){
                //proceed in local currency
                total += parseFloat(d.donation_amount);
            }else{
                //calculate total raised amount in USD for global display
                const amountInUsd = handleCurrencyConversion(parseFloat(d.donation_amount),d.donation_currency||'',currency||'')

                total += amountInUsd?.amount||0;
            }
            
        })

        return total;
    };

    // Currency conversion handler
    const handleCurrencyConversion = (amount: number, fromCurrency: string, toCurrency: string) => {
        return convertCurrency(amount, fromCurrency, toCurrency, exchangeRates?.rates || {});
    };

    

    

    // Detect user's browser currency from locale
    useEffect(() => {
   

        // Fetch exchange rates
        const fetchExchangeRates = async () => {
            const endpoint = "latest-currency-exchange-rates";
            const res = await makeGetRequest(endpoint);
            if (isErrorObject(res)) {
                const errorMessage = ErrorHandler(res);
                setError(errorMessage);
            } else {
                setExchangeRates(res.data);
            }
        };

        const fetchUserLocation = async () => {
            try {
              const response = await fetch("https://ipapi.co/json/");
              const locationData = await response.json();
              const userCurrency = locationData.currency || data.currency;
              setUserCurrencyCode(userCurrency.toUpperCase());
            } catch (err) {
              console.error("Failed to get user location:", err);
              setUserCurrencyCode("USD");
            }
          };

        fetchExchangeRates();
        fetchUserLocation();
    }, []);

    const raisedAmount = totalRaised();
    const totalRaisedInUsd = handleCurrencyConversion(raisedAmount, currency, "USD");

    return (
        <Card radius="sm" shadow="md" ml="xs" component={Link} {...linkProps} className={classes.card}>
            <Card.Section>
                {error && <p className="red">{error}</p>}
                <Image src={image} height={280} className={classes.image} />
            </Card.Section>

            <Card.Section pt={0} px="md" pb="md">
                <Stack>
                    <Text className={classes.title} lineClamp={1} fw={500} size="lg">
                        {title}
                    </Text>

                    <Group position="apart">
                        <Text size="xs" transform="uppercase" color="dimmed" fw={700}>
                            {country}
                        </Text>
                        <Badge variant="dot" color="secondary">
                            {category.category}
                        </Badge>
                    </Group>

                    {showActions && <Text lineClamp={3} size="sm">{description}</Text>}

                    <Progress value={getProgress(parseFloat(raised), parseFloat(target.toString()))} />

                    <Flex justify="space-between">
                        <Text> 
                        <b>
                            {userCurrencyCode === data.currency
                            ? `${data.currency} ${totalRaised()}`
                            : `USD ${formatCurrency(totalRaisedInUsd?.amount || 0)}`}
                        </b>
                        {userCurrencyCode === data.currency && (
                            <> ({data.currency} {formatCurrency(totalRaised())})</>
                        )}{" "}
                        raised
                        </Text>
                        <Text>
                        <b>{data.donations?.length}</b> donations
                        </Text>
                    </Flex>
                </Stack>
            </Card.Section>
        </Card>
    );
};

export default CampaignCard;
